import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller"
import { Purchase } from "@/models/purchase.model"
import { type IPurchase } from "@/interfaces/api/purchase.interface"
import { PurchaseRepository } from "@/repositories/purchase-repository"
import { usePurchaseStore } from "@/store/purchase.store"
import type { Moment } from "moment"
import type { PurchaseItem } from "@/models/purchase-item.model"
import { ModelFactory } from "@/factories/model-factory.ts"
import ModelManager from "@/services/model-manager.ts"
import { ODataQueryBuilder } from "@/builder/odata-query-builder.ts"

export default class PurchaseController extends GenericModelController<
  Purchase,
  IPurchase,
  PurchaseRepository
> {
  purchaseStore = usePurchaseStore()

  constructor() {
    const { pushPurchaseToRelatedObjects, deletePurchaseFromCollections } = usePurchaseStore()

    const storeFunctionsConfig = {
      onCreate: pushPurchaseToRelatedObjects,
      onDelete: deletePurchaseFromCollections
    }

    super(Purchase, storeFunctionsConfig, new PurchaseRepository())
  }

  public async applyPricesRetroactively(purchaseItem: PurchaseItem, retroactiveDate: Moment) {
    return this.apiFunctions.getRepository().applyPricesRetroactively(purchaseItem, retroactiveDate)
  }

  public async getOutstandingPurchases(query: ODataQueryBuilder = new ODataQueryBuilder()) {
    query.eq("is_outstanding", true)

    const data = await this.apiFunctions.getModels({ query })
    const models = ModelFactory.createModels(Purchase, data)
    this.purchaseStore.addToRetrievedPurchases(models)
    return models
  }

  public async getPurchasesById(purchaseIds: string[]) {
    const missingIds = []
    for (const id of purchaseIds) {
      if (!ModelManager.getInstance().exists(id)) {
        missingIds.push(id)
      }
    }

    if (missingIds.length === 0) {
      return [] // Nothing to fetch.
    }

    const query = new ODataQueryBuilder().in("id", missingIds)

    const data = await this.apiFunctions.getModels({ query })
    const models = ModelFactory.createModels(Purchase, data)
    this.purchaseStore.addToRetrievedPurchases(models)
    return models
  }
}
